/**
 * The three fields that identify an organization's admin.
 *
 * `admin_user_id` is the LEFT JOIN result, and it is the only field that
 * answers "does this user exist?". `admin_name` and `admin_username` are both
 * nullable columns in their own right, so a null in either one says nothing
 * about whether the row was found.
 */
export type OrgAdminIdentity = {
  admin_user_id: number | null;
  admin_name: string | null;
  admin_username: string | null;
};

/**
 * True when org_admin_user_id points at a user row that does not exist —
 * a dangling reference worth flagging in the UI.
 */
export const isAdminUserMissing = (admin: OrgAdminIdentity): boolean =>
  admin.admin_user_id == null;

/**
 * Primary label for the org admin. Distinguishes a dangling id ("Unknown
 * user") from a real user who simply has no name set ("Unnamed user", or
 * their username when they have one) — conflating the two would report a
 * present admin as missing.
 */
export const formatAdminDisplayName = (admin: OrgAdminIdentity): string => {
  if (admin.admin_name) return admin.admin_name;
  if (isAdminUserMissing(admin)) return "Unknown user";
  if (admin.admin_username) return `@${admin.admin_username}`;
  return "Unnamed user";
};

/**
 * Whether to append the username next to the name. Skipped when the name is
 * absent, because formatAdminDisplayName already promotes the username to the
 * primary slot and repeating it reads as two different people.
 */
export const shouldShowAdminUsername = (admin: OrgAdminIdentity): boolean =>
  Boolean(admin.admin_name) && Boolean(admin.admin_username);
